import * as React from 'react';
import { cn } from '../../lib/cn';
import { Label } from '../label/label';
import { HelpText } from '../help_text/help_text';

export interface FormFieldContextValue {
  /**
   * Space-separated ids of this field's currently-rendered description
   * elements (help text, inline error message) — `undefined` when neither is
   * rendered. Every consumer (`Input`, `Select`, `PasswordInput`,
   * `AutoGrowTextarea`, `StyledSelect`, ...) must use this value directly as
   * `aria-describedby` rather than re-deriving it from a raw id, so an id is
   * never referenced unless the element it points to actually exists in the
   * DOM (WCAG 1.3.1 / 4.1.2).
   */
  describedBy: string | undefined;
  ariaInvalid: boolean;
}

export const FormFieldContext = React.createContext<
  FormFieldContextValue | undefined
>(undefined);

export function useFormFieldContext(): FormFieldContextValue | undefined {
  return React.useContext(FormFieldContext);
}

export interface FormFieldProps {
  label?: React.ReactNode;
  /** Associates the label with an input via the `for` attribute. */
  htmlFor?: string;
  helpText?: string;
  /** Error state — pass `true` for a boolean indicator, or a string for an error message. */
  error?: React.ReactNode;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}

export function FormField({
  label,
  htmlFor,
  helpText,
  error,
  required,
  className,
  children,
}: FormFieldProps) {
  const has_error = Boolean(error);
  const errorId = React.useId();
  const helpTextId = React.useId();
  const show_error_msg =
    error !== undefined &&
    error !== null &&
    error !== false &&
    typeof error === 'string';
  const show_help_text = helpText !== undefined;

  // Only ever reference ids of elements actually rendered below — leaving a
  // help-text id in `aria-describedby` after the error clears (or vice
  // versa) is a dangling reference: screen readers resolve it to nothing.
  const describedBy =
    [show_help_text ? helpTextId : null, show_error_msg ? errorId : null]
      .filter((id): id is string => id !== null)
      .join(' ') || undefined;

  const child_with_aria = React.isValidElement(children)
    ? React.cloneElement(
        children as React.ReactElement<Record<string, unknown>>,
        {
          'aria-describedby': describedBy,
          'aria-invalid': has_error || undefined,
        },
      )
    : children;

  const contextValue: FormFieldContextValue = {
    describedBy,
    ariaInvalid: has_error,
  };

  return (
    <FormFieldContext.Provider value={contextValue}>
      <div className={cn('nv-field', has_error && 'nv-field-error', className)}>
        {label !== undefined && (
          <Label htmlFor={htmlFor} required={required}>
            {label}
          </Label>
        )}
        {child_with_aria}
        {show_help_text && (
          <HelpText id={helpTextId} error={has_error}>
            {helpText}
          </HelpText>
        )}
        {show_error_msg && (
          <p
            id={errorId}
            role="alert"
            className="text-[13px] text-[var(--color-danger)]"
          >
            {error}
          </p>
        )}
      </div>
    </FormFieldContext.Provider>
  );
}
